import React, { useState } from "react";
import { Link } from "react-router-dom";
import SectionCard from "./SectionCard";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Badge } from "components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Trophy, Coins, ChevronRight } from "lucide-react";

const formatCurrency = (amount) => {
  if (amount == null) return "N/A";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: "compact", maximumFractionDigits: 1 }).format(amount);
};

const TopFacultyCard = ({
  data,
  showAll = false,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSeeMore,
  seeMoreText = "View all",
  initialTab = "h_index",
}) => {
  const [activeTab, setActiveTab] = useState(initialTab || "h_index");

  React.useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);
  const byHIndex = data?.by_h_index || [];
  const byGrants = data?.by_grants || [];

  const renderFacultyList = (facultyList) => {
    const displayList = showAll ? facultyList : facultyList.slice(0, 5);
    return (
      <div className={`flex flex-col flex-1 gap-3 ${showAll ? "max-h-[460px] overflow-y-auto pr-2" : ""}`}>
        {displayList.map((faculty, idx) => {
          const subtitle =
            faculty.position ||
            faculty.department ||
            ((faculty.publication_count ?? faculty.pubs) != null
              ? `${(faculty.publication_count ?? faculty.pubs).toLocaleString()} pubs`
              : "");

          return (
            <Link
              key={faculty.id || idx}
              to={`/explore/faculty/${faculty.explore_slug || faculty.id}`}
              className="flex items-center justify-between p-2.5 h-[72px] rounded-lg border bg-card hover:bg-accent/50 hover:border-explore/40 transition-colors group cursor-pointer"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0 pr-3">
                <Avatar className="h-11 w-11 shrink-0 border-2 border-transparent group-hover:border-explore transition-colors">
                  <AvatarImage src={faculty.photo_url} alt={faculty.name} className="object-cover" />
                  <AvatarFallback className="bg-explore/10 text-explore font-semibold text-sm">
                    {faculty.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex flex-col justify-center gap-1 flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate group-hover:text-explore transition-colors">
                    {faculty.name}
                  </h4>
                  <p className="text-xs text-muted-foreground truncate" title={subtitle}>
                    {subtitle}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0 text-right">
                {/* Grants Column */}
                {(faculty.total_amount != null || faculty.grants_count != null) && (
                  <div className="flex flex-col items-end justify-center min-w-[55px]">
                    {faculty.total_amount != null && (
                      <span className="text-xs font-bold text-explore flex items-center gap-1">
                        <Coins className="w-3 h-3" />
                        {formatCurrency(faculty.total_amount)}
                      </span>
                    )}
                    {faculty.grants_count != null && (
                      <span className="text-[10px] text-muted-foreground uppercase font-medium">
                        {faculty.grants_count} Grants
                      </span>
                    )}
                  </div>
                )}
                {/* H-Index Column */}
                {faculty.h_index != null && (
                  <div className="flex flex-col items-end justify-center min-w-[48px]">
                    <Badge variant="secondary" className="flex items-center gap-1 font-medium bg-muted text-muted-foreground border-none text-xs py-1">
                      <Trophy className="w-3 h-3 text-explore" />
                      {faculty.h_index}
                    </Badge>
                  </div>
                )}
                <ChevronRight className="w-5 h-5 text-muted-foreground/50 group-hover:text-explore transition-colors shrink-0" />
              </div>
            </Link>
          );
        })}
      </div>
    );
  };

  return (
    <SectionCard
      title="Top Faculty Members"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!byHIndex.length && !byGrants.length}
      onRetry={onRetry}
      onSeeMore={onSeeMore ? () => onSeeMore(activeTab) : undefined}
      seeMoreText={seeMoreText}
      headerEnd={
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-[220px]">
          <TabsList className="grid w-full grid-cols-2 p-1 bg-muted/50 rounded-lg">
            <TabsTrigger 
              value="h_index" 
              className="text-xs data-[state=active]:bg-explore data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all"
            >
              By H-Index
            </TabsTrigger>
            <TabsTrigger 
              value="grants" 
              className="text-xs data-[state=active]:bg-explore data-[state=active]:text-white data-[state=active]:shadow-sm rounded-md transition-all"
            >
              By Grants
            </TabsTrigger>
          </TabsList>
        </Tabs>
      }
    >
      {activeTab === "h_index" ? renderFacultyList(byHIndex) : renderFacultyList(byGrants)}
    </SectionCard>
  );
};

export default TopFacultyCard;
